// pages/admin/AdminLandingPage.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./AdminLandingPage.css"; // Import the CSS file

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminLandingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("hero");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/landing-page`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load landing page data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_URL}/landing-page`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Landing page updated successfully!");
    } catch (error) {
      toast.error("Failed to save changes");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Helper functions for updating state
  const updateData = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const updateArrayItem = (arrayName, index, field, value) => {
    setData((prev) => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addArrayItem = (arrayName, newItem) => {
    setData((prev) => ({
      ...prev,
      [arrayName]: [...(prev[arrayName] || []), newItem],
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setData((prev) => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index),
    }));
  };

  const updateNestedArrayItem = (arrayName, index, nestedField, nestedIndex, value) => {
    setData((prev) => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) => {
        if (i !== index) return item;
        const newNestedArray = [...(item[nestedField] || [])];
        newNestedArray[nestedIndex] = value;
        return { ...item, [nestedField]: newNestedArray };
      }),
    }));
  };

  const addNestedArrayItem = (arrayName, index, nestedField, value) => {
    setData((prev) => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) => {
        if (i !== index) return item;
        return { ...item, [nestedField]: [...(item[nestedField] || []), value] };
      }),
    }));
  };

  const removeNestedArrayItem = (arrayName, index, nestedField, nestedIndex) => {
    setData((prev) => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) => {
        if (i !== index) return item;
        return {
          ...item,
          [nestedField]: item[nestedField].filter((_, ni) => ni !== nestedIndex),
        };
      }),
    }));
  };

  const updateFooterLink = (sectionIndex, linkIndex, field, value) => {
    setData((prev) => ({
      ...prev,
      footerLinks: prev.footerLinks.map((section, si) => {
        if (si !== sectionIndex) return section;
        return {
          ...section,
          links: section.links.map((link, li) =>
            li === linkIndex ? { ...link, [field]: value } : link
          ),
        };
      }),
    }));
  };

  const addFooterLink = (sectionIndex) => {
    setData((prev) => ({
      ...prev,
      footerLinks: prev.footerLinks.map((section, si) => {
        if (si !== sectionIndex) return section;
        return {
          ...section,
          links: [...section.links, { label: "", url: "" }],
        };
      }),
    }));
  };

  const removeFooterLink = (sectionIndex, linkIndex) => {
    setData((prev) => ({
      ...prev,
      footerLinks: prev.footerLinks.map((section, si) => {
        if (si !== sectionIndex) return section;
        return {
          ...section,
          links: section.links.filter((_, li) => li !== linkIndex),
        };
      }),
    }));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p style={{ marginTop: "1rem", color: "#666" }}>Loading...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="loading-container">
        <p style={{ color: "red", fontSize: "1.2rem" }}>Failed to load data</p>
        <button onClick={fetchData} className="btn btn-primary" style={{ marginTop: "1rem" }}>
          Retry
        </button>
      </div>
    );
  }

  const tabs = [
    { id: "hero", label: "🏠 Hero" },
    { id: "features", label: "⭐ Features" },
    { id: "statistics", label: "📊 Statistics" },
    { id: "pricing", label: "💰 Pricing" },
    { id: "testimonials", label: "💬 Testimonials" },
    { id: "faq", label: "❓ FAQ" },
    { id: "cta", label: "📢 CTA" },
    { id: "footer", label: "📋 Footer" },
    { id: "seo", label: "🔍 SEO" },
  ];

  const iconOptions = [
    { value: "chart", label: "📊 Chart/Analytics" },
    { value: "inventory", label: "📦 Inventory" },
    { value: "expiry", label: "⏰ Expiry/Time" },
    { value: "billing", label: "📋 Billing" },
    { value: "parties", label: "👥 Parties/Users" },
    { value: "reports", label: "📄 Reports" },
  ];

  return (
    <div className="admin-page">
      {/* Header */}
      <div className="admin-header">
        <div className="header-content">
          <div className="page-title">
            <h1>Landing Page Editor</h1>
            <p>Customize your website's landing page</p>
          </div>
          <div className="header-actions">
            <a href="/" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
              Preview
            </a>
            <button onClick={handleSave} disabled={saving} className="btn btn-primary">
              {saving ? <span className="spinner-small"></span> : null}
              {saving ? " Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      <div className="main-container">
        {/* Tabs */}
        <div className="tabs-container">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn ${activeTab === tab.id ? "active" : ""}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Sections */}
        <div className="content-area">
          {/* HERO SECTION */}
          {activeTab === "hero" && (
            <div className="section-card">
              <div className="card-header">
                <h2>Hero Section</h2>
                <p>The main banner area of your landing page</p>
              </div>
              <div className="card-body">
                <div className="form-grid">
                  <div>
                    <div className="form-group">
                      <label>Logo Text</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.logoText || ""}
                        onChange={(e) => updateData("logoText", e.target.value)}
                        placeholder="Your Brand Name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Hero Title</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.heroTitle || ""}
                        onChange={(e) => updateData("heroTitle", e.target.value)}
                        placeholder="Your main headline"
                      />
                    </div>
                    <div className="form-group">
                      <label>Hero Subtitle</label>
                      <textarea
                        className="form-textarea"
                        value={data.heroSubtitle || ""}
                        onChange={(e) => updateData("heroSubtitle", e.target.value)}
                        rows={3}
                        placeholder="A brief description of your product"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="form-group">
                      <label>Button Text</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.heroButtonText || ""}
                        onChange={(e) => updateData("heroButtonText", e.target.value)}
                        placeholder="Get Started"
                      />
                    </div>
                    <div className="form-group">
                      <label>Button Link</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.heroButtonLink || ""}
                        onChange={(e) => updateData("heroButtonLink", e.target.value)}
                        placeholder="/register"
                      />
                    </div>
                    <div className="form-group">
                      <label>Hero Image URL (Optional)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.heroImage || ""}
                        onChange={(e) => updateData("heroImage", e.target.value)}
                        placeholder="https://example.com/image.png"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* FEATURES SECTION */}
          {activeTab === "features" && (
            <>
              <div className="section-card">
                <div className="card-header">
                  <h2>Features Section Settings</h2>
                </div>
                <div className="card-body">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Section Title</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.featuresTitle || ""}
                        onChange={(e) => updateData("featuresTitle", e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Section Subtitle</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.featuresSubtitle || ""}
                        onChange={(e) => updateData("featuresSubtitle", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="section-card">
                <div className="card-header">
                  <h2>Features List</h2>
                </div>
                <div className="card-body">
                  {data.features?.map((feature, index) => (
                    <div key={index} className="list-item">
                      <div className="list-item-header">
                        <span className="list-item-title">Feature {index + 1}</span>
                        <button
                          onClick={() => removeArrayItem("features", index)}
                          className="btn btn-danger"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="form-grid-3">
                        <div className="form-group">
                          <label>Icon</label>
                          <select
                            className="form-select"
                            value={feature.icon || "chart"}
                            onChange={(e) => updateArrayItem("features", index, "icon", e.target.value)}
                          >
                            {iconOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Title</label>
                          <input
                            type="text"
                            className="form-input"
                            value={feature.title || ""}
                            onChange={(e) => updateArrayItem("features", index, "title", e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label>Description</label>
                          <input
                            type="text"
                            className="form-input"
                            value={feature.description || ""}
                            onChange={(e) => updateArrayItem("features", index, "description", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayItem("features", { icon: "chart", title: "", description: "" })}
                    className="btn btn-dashed"
                  >
                    + Add New Feature
                  </button>
                </div>
              </div>
            </>
          )}

          {/* STATISTICS SECTION */}
          {activeTab === "statistics" && (
            <div className="section-card">
              <div className="card-header">
                <h2>Statistics Section</h2>
              </div>
              <div className="card-body">
                <div className="toggle-wrapper">
                  <span className="list-item-title">Show Statistics Section</span>
                  <button
                    onClick={() => updateData("showStatistics", !data.showStatistics)}
                    className={`toggle-switch ${data.showStatistics ? "on" : ""}`}
                  >
                    <span className="toggle-slider" />
                  </button>
                </div>

                {data.statistics?.map((stat, index) => (
                  <div key={index} className="list-item">
                     <div className="form-grid-3">
                      <div className="form-group">
                        <label>Value</label>
                        <input
                          type="text"
                          className="form-input"
                          value={stat.value || ""}
                          onChange={(e) => updateArrayItem("statistics", index, "value", e.target.value)}
                          placeholder="e.g., 10,000+"
                        />
                      </div>
                      <div className="form-group">
                        <label>Label</label>
                        <input
                          type="text"
                          className="form-input"
                          value={stat.label || ""}
                          onChange={(e) => updateArrayItem("statistics", index, "label", e.target.value)}
                          placeholder="e.g., Active Users"
                        />
                      </div>
                      <div style={{display:'flex', alignItems: 'flex-end', paddingBottom: '2px'}}>
                         <button
                            onClick={() => removeArrayItem("statistics", index)}
                            className="btn btn-danger"
                          >
                            Remove
                          </button>
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => addArrayItem("statistics", { value: "", label: "" })}
                  className="btn btn-dashed"
                >
                  + Add New Statistic
                </button>
              </div>
            </div>
          )}

          {/* PRICING SECTION */}
          {activeTab === "pricing" && (
            <>
              <div className="section-card">
                <div className="card-header">
                  <h2>Pricing Section Settings</h2>
                </div>
                <div className="card-body">
                  <div className="toggle-wrapper">
                    <span className="list-item-title">Show Pricing Section</span>
                    <button
                      onClick={() => updateData("showPricing", !data.showPricing)}
                      className={`toggle-switch ${data.showPricing ? "on" : ""}`}
                    >
                      <span className="toggle-slider" />
                    </button>
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Section Title</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.pricingTitle || ""}
                        onChange={(e) => updateData("pricingTitle", e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Section Subtitle</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.pricingSubtitle || ""}
                        onChange={(e) => updateData("pricingSubtitle", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="section-card">
                <div className="card-header">
                  <h2>Pricing Plans</h2>
                </div>
                <div className="card-body">
                  {data.pricingPlans?.map((plan, index) => (
                    <div key={index} className="list-item">
                      <div className="list-item-header">
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span className="list-item-title">Plan {index + 1}</span>
                        </div>
                        <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={plan.isPopular || false}
                              onChange={(e) => updateArrayItem("pricingPlans", index, "isPopular", e.target.checked)}
                            />
                            Mark as Popular
                          </label>
                          <button
                            onClick={() => removeArrayItem("pricingPlans", index)}
                            className="btn btn-danger"
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      <div className="form-grid-4">
                        <div className="form-group">
                          <label>Plan Name</label>
                          <input
                            type="text"
                            className="form-input"
                            value={plan.name || ""}
                            onChange={(e) => updateArrayItem("pricingPlans", index, "name", e.target.value)}
                            placeholder="e.g., Basic"
                          />
                        </div>
                        <div className="form-group">
                          <label>Price</label>
                          <input
                            type="text"
                            className="form-input"
                            value={plan.price || ""}
                            onChange={(e) => updateArrayItem("pricingPlans", index, "price", e.target.value)}
                            placeholder="e.g., Rs999"
                          />
                        </div>
                        <div className="form-group">
                          <label>Period</label>
                          <input
                            type="text"
                            className="form-input"
                            value={plan.period || ""}
                            onChange={(e) => updateArrayItem("pricingPlans", index, "period", e.target.value)}
                            placeholder="e.g., /month"
                          />
                        </div>
                        <div className="form-group">
                          <label>Description</label>
                          <input
                            type="text"
                            className="form-input"
                            value={plan.description || ""}
                            onChange={(e) => updateArrayItem("pricingPlans", index, "description", e.target.value)}
                            placeholder="e.g., Perfect for small teams"
                          />
                        </div>
                      </div>

                      <div className="form-grid">
                        <div className="form-group">
                          <label>Button Text</label>
                          <input
                            type="text"
                            className="form-input"
                            value={plan.buttonText || ""}
                            onChange={(e) => updateArrayItem("pricingPlans", index, "buttonText", e.target.value)}
                            placeholder="Get Started"
                          />
                        </div>
                        <div className="form-group">
                          <label>Button Link</label>
                          <input
                            type="text"
                            className="form-input"
                            value={plan.buttonLink || ""}
                            onChange={(e) => updateArrayItem("pricingPlans", index, "buttonLink", e.target.value)}
                            placeholder="/register"
                          />
                        </div>
                      </div>

                      {/* Plan Features */}
                      <div className="form-group">
                        <label>Features</label>
                        {plan.features?.map((feature, fIndex) => (
                          <div key={fIndex} style={{ display: "flex", gap: "10px", marginBottom: "5px" }}>
                            <input
                              type="text"
                              className="form-input"
                              value={feature || ""}
                              onChange={(e) => updateNestedArrayItem("pricingPlans", index, "features", fIndex, e.target.value)}
                              placeholder="Feature description"
                            />
                            <button
                              onClick={() => removeNestedArrayItem("pricingPlans", index, "features", fIndex)}
                              className="btn btn-danger"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => addNestedArrayItem("pricingPlans", index, "features", "")}
                          className="btn btn-dashed"
                          style={{ padding: "0.5rem", marginTop: "5px", fontSize: "0.8rem" }}
                        >
                          + Add Feature
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayItem("pricingPlans", {
                      name: "",
                      price: "",
                      period: "/month",
                      description: "",
                      features: [],
                      isPopular: false,
                      buttonText: "Get Started",
                      buttonLink: "/register"
                    })}
                    className="btn btn-dashed"
                  >
                    + Add New Pricing Plan
                  </button>
                </div>
              </div>
            </>
          )}

          {/* TESTIMONIALS */}
          {activeTab === "testimonials" && (
            <>
              <div className="section-card">
                <div className="card-header">
                  <h2>Testimonials Section Settings</h2>
                </div>
                <div className="card-body">
                  <div className="toggle-wrapper">
                    <span className="list-item-title">Show Testimonials Section</span>
                    <button
                      onClick={() => updateData("showTestimonials", !data.showTestimonials)}
                      className={`toggle-switch ${data.showTestimonials ? "on" : ""}`}
                    >
                      <span className="toggle-slider" />
                    </button>
                  </div>
                  <div className="form-group">
                    <label>Section Title</label>
                    <input
                      type="text"
                      className="form-input"
                      value={data.testimonialsTitle || ""}
                      onChange={(e) => updateData("testimonialsTitle", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="section-card">
                <div className="card-header">
                  <h2>Testimonials List</h2>
                </div>
                <div className="card-body">
                  {data.testimonials?.map((testimonial, index) => (
                    <div key={index} className="list-item">
                      <div className="list-item-header">
                        <span className="list-item-title">Testimonial {index + 1}</span>
                        <button
                          onClick={() => removeArrayItem("testimonials", index)}
                          className="btn btn-danger"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="form-grid">
                        <div className="form-group">
                          <label>Name</label>
                          <input
                            type="text"
                            className="form-input"
                            value={testimonial.name || ""}
                            onChange={(e) => updateArrayItem("testimonials", index, "name", e.target.value)}
                          />
                        </div>
                         <div className="form-group">
                          <label>Role</label>
                          <input
                            type="text"
                            className="form-input"
                            value={testimonial.role || ""}
                            onChange={(e) => updateArrayItem("testimonials", index, "role", e.target.value)}
                             placeholder="e.g., CEO"
                          />
                        </div>
                         <div className="form-group">
                          <label>Company</label>
                          <input
                            type="text"
                            className="form-input"
                            value={testimonial.company || ""}
                            onChange={(e) => updateArrayItem("testimonials", index, "company", e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label>Rating (1-5)</label>
                          <select
                            className="form-select"
                            value={testimonial.rating || 5}
                            onChange={(e) => updateArrayItem("testimonials", index, "rating", parseInt(e.target.value))}
                          >
                            {[1, 2, 3, 4, 5].map((n) => (
                              <option key={n} value={n}>{n} Star{n > 1 ? "s" : ""}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Testimonial Content</label>
                        <textarea
                          className="form-textarea"
                          value={testimonial.content || ""}
                          onChange={(e) => updateArrayItem("testimonials", index, "content", e.target.value)}
                          rows={3}
                          placeholder="What the customer said about your product..."
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayItem("testimonials", {
                      name: "",
                      role: "",
                      company: "",
                      content: "",
                      rating: 5
                    })}
                    className="btn btn-dashed"
                  >
                    + Add New Testimonial
                  </button>
                </div>
              </div>
            </>
          )}

          {/* FAQ */}
          {activeTab === "faq" && (
            <>
              <div className="section-card">
                <div className="card-header">
                  <h2>FAQ Section Settings</h2>
                </div>
                <div className="card-body">
                  <div className="toggle-wrapper">
                    <span className="list-item-title">Show FAQ Section</span>
                    <button
                      onClick={() => updateData("showFAQ", !data.showFAQ)}
                      className={`toggle-switch ${data.showFAQ ? "on" : ""}`}
                    >
                      <span className="toggle-slider" />
                    </button>
                  </div>
                  <div className="form-group">
                    <label>Section Title</label>
                    <input
                      type="text"
                      className="form-input"
                      value={data.faqTitle || ""}
                      onChange={(e) => updateData("faqTitle", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="section-card">
                 <div className="card-header">
                  <h2>FAQ List</h2>
                </div>
                <div className="card-body">
                  {data.faqs?.map((faq, index) => (
                    <div key={index} className="list-item">
                      <div className="list-item-header">
                        <span className="list-item-title">FAQ {index + 1}</span>
                        <button
                          onClick={() => removeArrayItem("faqs", index)}
                          className="btn btn-danger"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="form-group">
                        <label>Question</label>
                        <input
                          type="text"
                          className="form-input"
                          value={faq.question || ""}
                          onChange={(e) => updateArrayItem("faqs", index, "question", e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Answer</label>
                        <textarea
                          className="form-textarea"
                          value={faq.answer || ""}
                          onChange={(e) => updateArrayItem("faqs", index, "answer", e.target.value)}
                          rows={3}
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayItem("faqs", { question: "", answer: "" })}
                    className="btn btn-dashed"
                  >
                    + Add New FAQ
                  </button>
                </div>
              </div>
            </>
          )}

           {/* CTA */}
          {activeTab === "cta" && (
            <div className="section-card">
              <div className="card-header">
                <h2>Call-to-Action Section</h2>
              </div>
              <div className="card-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>CTA Title</label>
                    <input
                      type="text"
                      className="form-input"
                      value={data.ctaTitle || ""}
                      onChange={(e) => updateData("ctaTitle", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>CTA Subtitle</label>
                    <input
                      type="text"
                      className="form-input"
                      value={data.ctaSubtitle || ""}
                      onChange={(e) => updateData("ctaSubtitle", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Button Text</label>
                    <input
                      type="text"
                      className="form-input"
                      value={data.ctaButtonText || ""}
                      onChange={(e) => updateData("ctaButtonText", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Button Link</label>
                    <input
                      type="text"
                      className="form-input"
                      value={data.ctaButtonLink || ""}
                      onChange={(e) => updateData("ctaButtonLink", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* FOOTER */}
          {activeTab === "footer" && (
            <>
              <div className="section-card">
                <div className="card-header">
                  <h2>Footer Content</h2>
                </div>
                <div className="card-body">
                  <div className="form-group">
                    <label>Footer Description</label>
                    <textarea
                      className="form-textarea"
                      value={data.footerDescription || ""}
                      onChange={(e) => updateData("footerDescription", e.target.value)}
                      rows={3}
                    />
                  </div>
                  <div className="form-grid-3">
                    <div className="form-group">
                      <label>Contact Email</label>
                      <input
                        type="email"
                        className="form-input"
                        value={data.contactEmail || ""}
                        onChange={(e) => updateData("contactEmail", e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact Phone</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.contactPhone || ""}
                        onChange={(e) => updateData("contactPhone", e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact Address</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.contactAddress || ""}
                        onChange={(e) => updateData("contactAddress", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="section-card">
                <div className="card-header">
                  <h2>Social Links</h2>
                </div>
                <div className="card-body">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Facebook URL</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.socialLinks?.facebook || ""}
                        onChange={(e) => updateData("socialLinks", { ...data.socialLinks, facebook: e.target.value })}
                        placeholder="https://facebook.com/..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Twitter URL</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.socialLinks?.twitter || ""}
                        onChange={(e) => updateData("socialLinks", { ...data.socialLinks, twitter: e.target.value })}
                        placeholder="https://twitter.com/..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Instagram URL</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.socialLinks?.instagram || ""}
                        onChange={(e) => updateData("socialLinks", { ...data.socialLinks, instagram: e.target.value })}
                        placeholder="https://instagram.com/..."
                      />
                    </div>
                    <div className="form-group">
                      <label>LinkedIn URL</label>
                      <input
                        type="text"
                        className="form-input"
                        value={data.socialLinks?.linkedin || ""}
                        onChange={(e) => updateData("socialLinks", { ...data.socialLinks, linkedin: e.target.value })}
                        placeholder="https://linkedin.com/..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="section-card">
                <div className="card-header">
                  <h2>Footer Link Sections</h2>
                </div>
                <div className="card-body">
                  {data.footerLinks?.map((section, sIndex) => (
                    <div key={sIndex} className="list-item">
                      <div className="list-item-header">
                         <div style={{flex: 1, marginRight: '1rem'}}>
                           <label style={{marginBottom: '5px', display: 'block', fontSize: '0.875rem', fontWeight: 500}}>Section Title</label>
                            <input
                              type="text"
                              className="form-input"
                              value={section.title || ""}
                              onChange={(e) => {
                                const newFooterLinks = [...data.footerLinks];
                                newFooterLinks[sIndex].title = e.target.value;
                                updateData("footerLinks", newFooterLinks);
                              }}
                            />
                         </div>
                        <button
                          onClick={() => removeArrayItem("footerLinks", sIndex)}
                          className="btn btn-danger"
                          style={{marginTop: '1.5rem'}}
                        >
                          Remove Section
                        </button>
                      </div>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                        {section.links?.map((link, lIndex) => (
                          <div key={lIndex} style={{display: 'flex', gap: '0.5rem'}}>
                            <input
                              type="text"
                              className="form-input"
                              value={link.label || ""}
                              onChange={(e) => updateFooterLink(sIndex, lIndex, "label", e.target.value)}
                              placeholder="Link Label"
                            />
                            <input
                              type="text"
                              className="form-input"
                              value={link.url || ""}
                              onChange={(e) => updateFooterLink(sIndex, lIndex, "url", e.target.value)}
                              placeholder="Link URL"
                            />
                            <button
                              onClick={() => removeFooterLink(sIndex, lIndex)}
                              className="btn btn-danger"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => addFooterLink(sIndex)}
                          className="btn btn-dashed"
                          style={{padding: '0.5rem', fontSize: '0.875rem'}}
                        >
                          + Add Link
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayItem("footerLinks", { title: "", links: [] })}
                    className="btn btn-dashed"
                  >
                    + Add New Footer Section
                  </button>
                </div>
              </div>
            </>
          )}

          {/* SEO SECTION */}
          {activeTab === "seo" && (
            <div className="section-card">
              <div className="card-header">
                <h2>SEO Settings</h2>
                <p>Optimize your landing page for search engines</p>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label>Meta Title</label>
                  <input
                    type="text"
                    className="form-input"
                    value={data.metaTitle || ""}
                    onChange={(e) => updateData("metaTitle", e.target.value)}
                    placeholder="Your page title for search engines"
                  />
                  <p style={{fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem'}}>Recommended: 50-60 characters</p>
                </div>
                <div className="form-group">
                  <label>Meta Description</label>
                  <textarea
                    className="form-textarea"
                    value={data.metaDescription || ""}
                    onChange={(e) => updateData("metaDescription", e.target.value)}
                    rows={3}
                    placeholder="A brief description of your page for search results"
                  />
                  <p style={{fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem'}}>Recommended: 150-160 characters</p>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Primary Color</label>
                    <div className="color-picker-group">
                      <input
                        type="color"
                        className="color-input"
                        value={data.primaryColor || "#2563eb"}
                        onChange={(e) => updateData("primaryColor", e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-input"
                        value={data.primaryColor || "#2563eb"}
                        onChange={(e) => updateData("primaryColor", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Secondary Color</label>
                    <div className="color-picker-group">
                      <input
                        type="color"
                        className="color-input"
                        value={data.secondaryColor || "#7c3aed"}
                        onChange={(e) => updateData("secondaryColor", e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-input"
                        value={data.secondaryColor || "#7c3aed"}
                        onChange={(e) => updateData("secondaryColor", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}